import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { loaders, sections } from 'virtual:i18n-sections';

import { BLOG_SECTIONS, NOT_FOUND_SECTIONS, PAGE_SECTIONS, UNLOCALIZED_PAGE_SECTIONS } from './i18n-sections.js';
import { findSlugKey, langs } from './utils/languageUtils.js';
import { canonicalPath } from './utils/siteUrl.js';

export function detectLang(pathname) {
    const parts = pathname.split('/');
    if (langs.includes(parts[1])) return parts[1];
    if (parts[1] === 'blog' && langs.includes(parts[2])) return parts[2];
    return 'en';
}

function sectionsFor(pathname) {
    if (pathname.split('/')[1] === 'blog') return ['shell', ...BLOG_SECTIONS];
    const key = findSlugKey(pathname);
    if (key !== null) {
        if (!PAGE_SECTIONS[key]) throw new Error(`The ${key} page declares no sections in i18n-sections.js`);
        return ['shell', 'metas', ...PAGE_SECTIONS[key]];
    }
    return ['shell', ...(UNLOCALIZED_PAGE_SECTIONS[canonicalPath(pathname).slice(1)] ?? NOT_FOUND_SECTIONS)];
}

// A fresh object per load: i18next merges a bundle into the store by reference, so a shared one would
// absorb every chunk added after it, and carry them into the next page the prerender renders.
const loadChunk = async (lang, name) => JSON.parse((await loaders[lang][name]()).default);

// No fallback language on either side: the browser holds only the page's language, so a key the
// prerender filled from another one would not hydrate.
const init = async (lng, extra) =>
    i18n.use(initReactI18next).init({ fallbackLng: false, supportedLngs: langs, lng, resources: {}, ...extra });

const added = new Map();

function addChunk(lang, name) {
    const key = `${lang}/${name}`;
    if (!added.has(key)) {
        added.set(
            key,
            loadChunk(lang, name).then(
                (chunk) => i18n.addResourceBundle(lang, 'translation', chunk, true, true),
                (error) => {
                    added.delete(key);
                    throw error;
                },
            ),
        );
    }
    return added.get(key);
}

/** Loads into the browser what the page at `pathname` renders: its language, and only its sections. */
export async function loadPageTranslations(pathname) {
    const lang = detectLang(pathname);
    if (!i18n.isInitialized) await init(lang);
    await Promise.all(sectionsFor(pathname).map((name) => addChunk(lang, name)));
}

let missingKeys = new Set();
let translation = {};
const translations = new Map();

const valueAt = (source, key) => key.split('.').reduce((node, part) => node?.[part], source);

function wholeTranslation(lang) {
    if (!translations.has(lang)) {
        translations.set(
            lang,
            Promise.all(sections.map(async (name) => loadChunk(lang, name))).then((chunks) =>
                Object.assign({}, ...chunks),
            ),
        );
    }
    return translations.get(lang);
}

/** Leaves i18n holding exactly what the browser will load for the page at `pathname`, and returns the
 * keys the page asks for outside of it. The caller renders one page at a time, which this relies on. */
export async function preparePrerender(pathname) {
    const lang = detectLang(pathname);
    const chunks = await Promise.all(sectionsFor(pathname).map(async (name) => loadChunk(lang, name)));
    translation = await wholeTranslation(lang);
    if (!i18n.isInitialized) {
        // Only a key the language has counts: one it lacks renders its own fallback on both sides.
        const missingKeyHandler = (_lngs, _ns, key) => {
            if (valueAt(translation, key) !== undefined) missingKeys.add(key);
        };
        await init(lang, { saveMissing: true, missingKeyHandler });
    }
    langs.forEach((l) => i18n.removeResourceBundle(l, 'translation'));
    chunks.forEach((chunk) => i18n.addResourceBundle(lang, 'translation', chunk, true, true));
    await i18n.changeLanguage(lang);
    missingKeys = new Set();
    return missingKeys;
}

export const initI18n = (location) => {
    if (!i18n.isInitialized) throw new Error('i18n used before its language was loaded');
    const lang = detectLang(location.pathname);
    if (i18n.language !== lang) void i18n.changeLanguage(lang);
    return i18n;
};
